import {useEffect, useState, useMemo, useRef} from 'react'
import {Link, useNavigate, useParams, Routes, Route} from 'react-router-dom'
import {useTranslation} from 'react-i18next'

import {Card4} from '../../../../../../_metronic/partials/content/cards/Card4'
import {useViewLandContext} from './ViewLandContext'
import {LoadingCard} from '../../../../../../_metronic/elements/Loading'
import {useViewImageContext} from './Images/ViewImageContext'
import {KTSVG} from '../../../../../../_metronic/helpers'
import {Ambients, ViewAmbientProvider, DialogAmbient, useViewAmbientContext} from './Ambients'
import {useMapLegendContext} from './Helpers/MapLegend'
import Layers from './Layers'
import Comparison from './Comparison'
import Images from './Images'
import Prescription from './Prescription'
import ImageAmbient from './ImageAmbients'
import UpdateLand from '../UpdateLand'
import DeleteLand from '../DeleteLand'
import DeleteAmbient from './Ambients/DeleteAmbient'
import NatalseedsLandMenu from './Natalseeds/NatalseedsLandMenu'
import NatalseedsCampaign from './Natalseeds/NatalseedsCampaign'
import './ViewLand.style.scss'
import {useMapLayersV2} from '../../../Map/MapLayersV2Context'
import {useIsMobile} from '../../../../../shared/responsive'
import {formatHectares} from '../../../../../shared/numberFormat'
import {useDrawV2} from '../../../Map/Draw/DrawV2Context'
import {ChatGptLogo, getCopilotLayerImageType, SpecificCopilot} from '../../../Copilot'
import {COPILOT_ENABLED} from '../../../../../config/featureFlags'

const LandMenu = () => {
  const {t} = useTranslation()
  const viewLandContext = useViewLandContext()
  const tenant_domain = useMemo(() => {
    return window.tenant_domain
  }, [])

  return (
    <>
      {tenant_domain == 'natalseeds.app' && <NatalseedsLandMenu />}
      <div className='land-menu-grid'>
        <div
          className='land-menu-item'
          onClick={(e) => {
            e.preventDefault()
            viewLandContext.navigateToSection('images')
          }}
        >
          <Card4
            icon='/media/icons/duotune/abstract/abs027.svg'
            title={t('lands:view.menu.images')}
            active={viewLandContext.activeSection === 'images'}
          />
        </div>
        <div
          className='land-menu-item'
          onClick={(e) => {
            e.preventDefault()
            viewLandContext.navigateToSection('ambients')
          }}
        >
          <Card4
            icon='/media/icons/duotune/maps/map002.svg'
            title={t('lands:view.menu.ambients')}
            active={viewLandContext.activeSection === 'ambients'}
          />
        </div>
        <div
          className='land-menu-item'
          onClick={(e) => {
            e.preventDefault()
            viewLandContext.navigateToSection('comparison')
          }}
        >
          <Card4
            icon='/media/icons/duotune/maps/map009.svg'
            title={t('lands:view.menu.comparison')}
            active={viewLandContext.activeSection === 'comparison'}
          />
        </div>
        <div
          className='land-menu-item'
          onClick={(e) => {
            e.preventDefault()
            viewLandContext.navigateToSection('layers')
          }}
        >
          <Card4
            icon='/media/icons/duotune/arrows/arr065.svg'
            title={t('lands:view.menu.layers')}
            active={viewLandContext.activeSection === 'layers'}
          />
        </div>
      </div>
    </>
  )
}

const LandMobileTabButton = ({active, label, onSelect, to}) => {
  return (
    <Link
      to={to}
      className={`view-land-mobile-tab ${active ? 'active' : ''}`.trim()}
      onClick={(event) => {
        event.preventDefault()
        onSelect()
      }}
    >
      <span className='view-land-mobile-tab__label'>{label}</span>
    </Link>
  )
}

const LandMobileSection = ({title, toolbar = null, children}) => {
  return (
    <div className='view-land-mobile-panel'>
      <div className='view-land-mobile-panel__header'>
        <h3 className='view-land-mobile-panel__title'>{title}</h3>
        {toolbar}
      </div>
      <div className='view-land-mobile-panel__body'>{children}</div>
    </div>
  )
}

const PrescriptionDialog = () => {
  const viewImageContext = useViewImageContext()
  const viewAmbientContext = useViewAmbientContext()
  return (
    <Prescription
      open={viewImageContext.openPrescription}
      setOpen={viewImageContext.setOpenPrescription}
      ambient={
        viewAmbientContext.selectedElement === 'imageAmbientHd'
          ? viewAmbientContext.imageAmbientHdData
          : viewAmbientContext.selectedElement === 'imageAmbient'
          ? viewAmbientContext.imageAmbientData
          : viewAmbientContext.selectedElement === 'ambientHd'
          ? viewAmbientContext.ambientHdData
          : viewAmbientContext.ambientData
      }
    />
  )
}

const ImageAmbientDialog = () => {
  const viewImageContext = useViewImageContext()
  const viewAmbientContext = useViewAmbientContext()
  return (
    <ImageAmbient
      open={viewImageContext.openImageAmbient}
      setOpen={viewImageContext.setOpenImageAmbient}
      ambient={
        viewAmbientContext.selectedElement === 'imageAmbientHd'
          ? viewAmbientContext.imageAmbientHdData
          : viewAmbientContext.imageAmbientData
      }
    />
  )
}

const ViewLand = () => {
  const [updateOpen, setUpdateOpen] = useState(false)
  const [deleteLand, setDeleteLand] = useState(false)
  const [copilotOpen, setCopilotOpen] = useState(false)
  const [pendingCopilotImageType, setPendingCopilotImageType] = useState(null)
  const mapLegendContext = useMapLegendContext()
  const mapLayers = useMapLayersV2()
  const fitMapRef = useRef(mapLayers.fit)
  fitMapRef.current = mapLayers.fit
  const removeAmbientLayerRef = useRef(mapLayers.land?.ambient?.remove)
  removeAmbientLayerRef.current = mapLayers.land?.ambient?.remove
  const hideAmbientLegendRef = useRef(mapLegendContext.hideAmbientLegend)
  hideAmbientLegendRef.current = mapLegendContext.hideAmbientLegend
  const clearVisibleLegendsRef = useRef(mapLegendContext.clearVisibleLegends)
  clearVisibleLegendsRef.current = mapLegendContext.clearVisibleLegends
  const viewLandContext = useViewLandContext()
  const viewImageContext = useViewImageContext()
  const {land_id} = useParams()
  const {t} = useTranslation()
  const isMobile = useIsMobile()
  const draw = useDrawV2()
  const navigate = useNavigate()
  const tenant_domain = useMemo(() => {
    return window.tenant_domain
  }, [])
  const mobileSection = viewLandContext.activeSection
  const isRouteEditing = viewLandContext.isRouteEditing
  const previousSectionRef = useRef(viewLandContext.activeSection)
  const stopViewingLandRef = useRef(viewLandContext.stopViewingLand)
  stopViewingLandRef.current = viewLandContext.stopViewingLand
  const clearMap = () => {
    mapLegendContext.deleteAll()
    viewImageContext.closeAllImages()
  }
  const handleCopilotAction = (action) => {
    if (action?.type === 'open_report' && action.reportId) {
      setCopilotOpen(false)
      navigate(`/gis/reports/economic/view/${action.reportId}`)
      return
    }

    const targetImageType = getCopilotLayerImageType(action)
    if (targetImageType) {
      setCopilotOpen(false)
      setPendingCopilotImageType(targetImageType)
      viewLandContext.navigateToSection('images')
      return
    }

    if (action?.type === 'open_historical_ambient') {
      setCopilotOpen(false)
      viewLandContext.navigateToSection('ambients')
    }
  }

  useEffect(() => {
    const land = viewLandContext.land
    if (!pendingCopilotImageType || !land?.land_id || viewImageContext.loading) {
      return
    }

    viewImageContext.changeImageType(land, pendingCopilotImageType)
    setPendingCopilotImageType(null)
  }, [pendingCopilotImageType, viewImageContext, viewLandContext.land])

  useEffect(() => {
    viewLandContext.setLandId(land_id)
    mapLegendContext.clearVisibleLegends()
    mapLegendContext.recoverIfExists(land_id)
  }, [land_id])

  useEffect(() => {
    if (!land_id) {
      return
    }

    viewImageContext.ensureLandDates(land_id).catch(() => {})
    // ensureLandDates identity changes when the dates cache updates; land dates only need prefetch per land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [land_id])

  useEffect(() => {
    if (viewLandContext.land != null) {
      viewImageContext.addLand(viewLandContext.land)
      mapLegendContext.clearVisibleLegends()
      mapLegendContext.recoverIfExists(viewLandContext.land.land_id)
    }
  }, [
    viewLandContext.land?.geometry_hash,
    viewLandContext.land?.geometryHash,
    viewLandContext.land?.land_id,
    viewLandContext.land?.polygons,
    viewLandContext.land?.polygon,
  ])

  useEffect(() => {
    const currentSection = viewLandContext.activeSection
    const previousSection = previousSectionRef.current

    if (!land_id || currentSection == null) {
      previousSectionRef.current = currentSection
      return
    }

    previousSectionRef.current = currentSection
    if (previousSection !== currentSection) {
      if (previousSection === 'ambients' && currentSection !== 'ambients') {
        removeAmbientLayerRef.current?.({landId: land_id})
        hideAmbientLegendRef.current?.(land_id)
      }

      const cancelFits = []
      const frameId = window.requestAnimationFrame(() => {
        cancelFits.push(fitMapRef.current(land_id))
      })

      const timerId = window.setTimeout(() => {
        cancelFits.push(fitMapRef.current(land_id))
      }, 180)

      return () => {
        window.cancelAnimationFrame(frameId)
        window.clearTimeout(timerId)
        cancelFits.forEach((cancel) => cancel?.())
      }
    }
  }, [land_id, viewLandContext.activeSection])

  useEffect(() => {
    return () => {
      clearVisibleLegendsRef.current?.()
      stopViewingLandRef.current(land_id)
    }
  }, [land_id])

  if (!viewLandContext.land || viewLandContext.land.land_id !== land_id) {
    return <LoadingCard className='view-land-container displayable' />
  }

  const area = formatHectares(viewLandContext.land.ha || 0)

  const renderMobileSection = () => {
    const toolbar = viewImageContext.hasCurrentLandMapOverlay ? (
      <button
        type='button'
        onClick={clearMap}
        className='btn btn-sm btn-light-danger view-land-mobile-panel__clear'
      >
        <KTSVG
          path='/media/icons/duotune/general/gen040.svg'
          className='svg-icon svg-icon-3 me-1'
        />
        <span>{t('common:toolbar.buttons.remove_images')}</span>
      </button>
    ) : null

    if (mobileSection === 'images') {
      return (
        <LandMobileSection title={t('lands:view.menu.images')} toolbar={toolbar}>
          <Images />
        </LandMobileSection>
      )
    }

    if (mobileSection === 'ambients') {
      return (
        <LandMobileSection title={t('lands:view.menu.ambients')} toolbar={toolbar}>
          <Ambients />
        </LandMobileSection>
      )
    }

    if (mobileSection === 'layers') {
      return (
        <LandMobileSection title={t('lands:view.menu.layers')} toolbar={toolbar}>
          <Layers />
        </LandMobileSection>
      )
    }

    if (mobileSection === 'comparison') {
      return (
        <LandMobileSection title={t('lands:view.menu.comparison')} toolbar={toolbar}>
          <Comparison />
        </LandMobileSection>
      )
    }

    return null
  }

  return (
    <>
      <ViewAmbientProvider>
        {isMobile ? (
          <>
            {!isRouteEditing && !draw.isDrawing && (
              <div className='view-land-mobile-shell displayable'>
                <div className='card shadow-sm view-land-container right-card-data view-land-mobile-card'>
                  <div className='view-land-mobile-card__header'>
                    <button
                      type='button'
                      className='btn btn-sm btn-light view-land-mobile-card__back'
                      onClick={viewLandContext.goBackToField}
                    >
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr063.svg'
                        className='svg-icon svg-icon-2'
                      />
                      <span>{t('common:navigation.back_field')}</span>
                    </button>

                    <div className='view-land-mobile-card__eyebrow'>{t('fields:labels.land')}</div>
                    <div className='view-land-mobile-card__title-row'>
                      <h2 className='view-land-mobile-card__title'>{viewLandContext.land.name}</h2>
                      <div className='view-land-mobile-card__header-actions'>
                        <button
                          type='button'
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            mapLayers.fit(land_id)
                          }}
                          className='btn btn-icon btn-sm view-land-mobile-card__header-action'
                          aria-label={t('lands:view.buttons.focus')}
                          title={t('lands:view.buttons.focus')}
                        >
                          <KTSVG
                            path='/media/icons/duotune/maps/map007.svg'
                            className='svg-icon svg-icon-2'
                          />
                        </button>
                        <button
                          type='button'
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            viewLandContext.goToUpdate()
                          }}
                          className='btn btn-icon btn-sm view-land-mobile-card__header-action view-land-mobile-card__header-action--primary'
                          aria-label={t('lands:view.buttons.update')}
                          title={t('lands:view.buttons.update')}
                        >
                          <KTSVG
                            path='/media/icons/duotune/art/art005.svg'
                            className='svg-icon svg-icon-2'
                          />
                        </button>
                        <button
                          type='button'
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            setDeleteLand(true)
                          }}
                          className='btn btn-icon btn-sm view-land-mobile-card__header-action view-land-mobile-card__header-action--danger'
                          aria-label={t('lands:view.buttons.delete')}
                          title={t('lands:view.buttons.delete')}
                        >
                          <KTSVG
                            path='/media/icons/duotune/general/gen027.svg'
                            className='svg-icon svg-icon-2'
                          />
                        </button>
                      </div>
                    </div>

                    <div className='view-land-mobile-card__meta'>
                      <span className='view-land-mobile-card__pill'>
                        {viewLandContext.land.field.name}
                      </span>
                      <span className='view-land-mobile-card__pill'>{area}</span>
                    </div>
                    {COPILOT_ENABLED && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary view-land-copilot-button'
                        onClick={() => setCopilotOpen(true)}
                      >
                        <ChatGptLogo className='view-land-copilot-button__logo' />
                        <span>{t('copilot:button')}</span>
                      </button>
                    )}
                  </div>

                  <div
                    className='view-land-mobile-card__tabs'
                    role='tablist'
                    aria-label='Plot sections'
                  >
                    <LandMobileTabButton
                      active={mobileSection === 'images'}
                      label={t('lands:view.menu.images')}
                      onSelect={() => viewLandContext.navigateToSection('images')}
                      to={viewLandContext.getSectionHref('images')}
                    />
                    <LandMobileTabButton
                      active={mobileSection === 'ambients'}
                      label={t('lands:view.menu.ambients')}
                      onSelect={() => viewLandContext.navigateToSection('ambients')}
                      to={viewLandContext.getSectionHref('ambients')}
                    />
                    <LandMobileTabButton
                      active={mobileSection === 'comparison'}
                      label={t('lands:view.menu.comparison')}
                      onSelect={() => viewLandContext.navigateToSection('comparison')}
                      to={viewLandContext.getSectionHref('comparison')}
                    />
                    <LandMobileTabButton
                      active={mobileSection === 'layers'}
                      label={t('lands:view.menu.layers')}
                      onSelect={() => viewLandContext.navigateToSection('layers')}
                      to={viewLandContext.getSectionHref('layers')}
                    />
                  </div>

                  {tenant_domain == 'natalseeds.app' && (
                    <div className='view-land-mobile-tenant-menu'>
                      <NatalseedsLandMenu />
                    </div>
                  )}

                  {renderMobileSection()}
                </div>
              </div>
            )}
            {isRouteEditing && <UpdateLand open={!draw.isDrawing} setOpen={setUpdateOpen} />}
          </>
        ) : (
          <div className='card shadow-sm view-land-container displayable right-card-data view-land-card'>
            <div className='card-header view-land-card__header'>
              <div
                className='view-land-card__title-group cursor-pointer'
                data-bs-toggle='collapse'
                data-bs-target='#kt_view_land_collapsible'
                aria-expanded='true'
                aria-controls='kt_view_land_collapsible'
              >
                <span className='view-land-card__title'>{viewLandContext.land.name}</span>
                <span className='view-land-card__meta'>
                  {formatHectares(viewLandContext.land.ha)}
                </span>
              </div>

              <div
                className='card-toolbar view-land-card__toolbar'
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <button
                  type='button'
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    mapLayers.fit(land_id)
                  }}
                  className='btn btn-color-muted btn-active-color-primary btn-sm view-land-card__focus'
                >
                  <KTSVG
                    path='/media/icons/duotune/maps/map007.svg'
                    className='svg-icon svg-icon-1 view-land-card__focus-icon'
                  />
                </button>
                <button
                  type='button'
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    setDeleteLand(true)
                  }}
                  className='btn btn-sm btn-light-danger view-land-card__action-button'
                >
                  {t('lands:view.buttons.delete')}
                </button>
                <button
                  type='button'
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    setUpdateOpen(true)
                  }}
                  className='btn btn-sm btn-light-primary view-land-card__action-button'
                >
                  {t('lands:view.buttons.update')}
                </button>
              </div>
            </div>
            <div id='kt_view_land_collapsible' className='collapse show'>
              <div className='card-body view-land-card__body'>
                <LandMenu />
                <div className='view-land-content'>
                  <Routes>
                    <Route path='/' element={<Images />} />
                    <Route path='/layers' element={<Layers />} />
                    <Route path='/comparison' element={<Comparison />} />
                    <Route path='/ambients' element={<Ambients />} />
                  </Routes>
                </div>
              </div>
            </div>
          </div>
        )}
        <PrescriptionDialog />
        <ImageAmbientDialog />
        <DialogAmbient />
        <DeleteAmbient />
      </ViewAmbientProvider>
      {!isMobile && <UpdateLand open={updateOpen} setOpen={setUpdateOpen} />}
      <DeleteLand open={deleteLand} setOpen={setDeleteLand} />
      {COPILOT_ENABLED && isMobile && (
        <SpecificCopilot
          open={copilotOpen}
          setOpen={setCopilotOpen}
          landId={land_id}
          onAction={handleCopilotAction}
        />
      )}
      {tenant_domain == 'natalseeds.app' && <NatalseedsCampaign />}
    </>
  )
}

export default ViewLand
